/**
 * Admin Role Levels
 * Level 0: SUPER_ADMIN - Total access including system config
 * Level 1: ADMIN - Full management except system config
 * Level 2: MODERATOR - Content moderation only
 * Level 3: FINANCE - Financial operations
 * Level 4: SUPPORT - Customer support
 * Level 5: VIEWER - Read-only access
 */
export enum AdminRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR',
  FINANCE = 'FINANCE',
  SUPPORT = 'SUPPORT',
  VIEWER = 'VIEWER',
}

export const AdminRoleLevel: Record<AdminRole, number> = {
  [AdminRole.SUPER_ADMIN]: 0,
  [AdminRole.ADMIN]: 1,
  [AdminRole.MODERATOR]: 2,
  [AdminRole.FINANCE]: 3,
  [AdminRole.SUPPORT]: 4,
  [AdminRole.VIEWER]: 5,
};

/**
 * Granular Admin Permissions (35+)
 */
export enum AdminPermission {
  // Users
  USERS_READ = 'users.read',
  USERS_WRITE = 'users.write',
  USERS_DELETE = 'users.delete',
  USERS_EXPORT = 'users.export',

  // Cards
  CARDS_READ = 'cards.read',
  CARDS_WRITE = 'cards.write',
  CARDS_DELETE = 'cards.delete',
  CARDS_MODERATE = 'cards.moderate',

  // Partners (#1088) — n'existait pas alors que resources/partners/ + le
  // gate AdminMenu.tsx (canAccess(..., [AdminPermission.PARTNERS_READ]))
  // le référencent déjà. Seul PARTNERS_READ est réellement lu quelque part
  // dans le code (menu) — pas de WRITE/DELETE inventé sans usage réel.
  PARTNERS_READ = 'partners.read',

  // Plans & Subscriptions
  PLANS_READ = 'plans.read',
  PLANS_WRITE = 'plans.write',
  PLANS_DELETE = 'plans.delete',
  SUBSCRIPTIONS_READ = 'subscriptions.read',
  SUBSCRIPTIONS_WRITE = 'subscriptions.write',
  SUBSCRIPTIONS_CANCEL = 'subscriptions.cancel',

  // Finance
  FINANCE_READ = 'finance.read',
  FINANCE_WRITE = 'finance.write',
  FINANCE_EXPORT = 'finance.export',
  FINANCE_REFUND = 'finance.refund',

  // Rewards
  REWARDS_READ = 'rewards.read',
  REWARDS_WRITE = 'rewards.write',
  REWARDS_CONFIGURE = 'rewards.configure',

  // Academy
  ACADEMY_READ = 'academy.read',
  ACADEMY_WRITE = 'academy.write',
  ACADEMY_CERTIFY = 'academy.certify',

  // Support
  SUPPORT_READ = 'support.read',
  SUPPORT_WRITE = 'support.write',
  SUPPORT_ESCALATE = 'support.escalate',

  // Moderation
  MODERATION_READ = 'moderation.read',
  MODERATION_APPROVE = 'moderation.approve',
  MODERATION_REJECT = 'moderation.reject',
  MODERATION_BAN = 'moderation.ban',

  // Audit
  AUDIT_READ = 'audit.read',
  AUDIT_EXPORT = 'audit.export',

  // Analytics
  ANALYTICS_READ = 'analytics.read',
  ANALYTICS_EXPORT = 'analytics.export',

  // Configuration
  CONFIG_READ = 'config.read',
  CONFIG_WRITE = 'config.write',

  // Notifications
  NOTIFICATIONS_READ = 'notifications.read',
  NOTIFICATIONS_WRITE = 'notifications.write',
  NOTIFICATIONS_SEND = 'notifications.send',

  // Guard Services (Services de Garde)
  GUARD_READ = 'guard.read',
  GUARD_WRITE = 'guard.write',
  GUARD_IMPORT = 'guard.import',
}

/**
 * Role to Permissions mapping
 */
export const RolePermissions: Record<AdminRole, AdminPermission[]> = {
  [AdminRole.SUPER_ADMIN]: Object.values(AdminPermission), // All permissions

  [AdminRole.ADMIN]: [
    // All except CONFIG_WRITE
    AdminPermission.USERS_READ,
    AdminPermission.USERS_WRITE,
    AdminPermission.USERS_DELETE,
    AdminPermission.USERS_EXPORT,
    AdminPermission.CARDS_READ,
    AdminPermission.CARDS_WRITE,
    AdminPermission.CARDS_DELETE,
    AdminPermission.CARDS_MODERATE,
    AdminPermission.PARTNERS_READ,
    AdminPermission.PLANS_READ,
    AdminPermission.PLANS_WRITE,
    AdminPermission.PLANS_DELETE,
    AdminPermission.SUBSCRIPTIONS_READ,
    AdminPermission.SUBSCRIPTIONS_WRITE,
    AdminPermission.SUBSCRIPTIONS_CANCEL,
    AdminPermission.FINANCE_READ,
    AdminPermission.FINANCE_WRITE,
    AdminPermission.FINANCE_EXPORT,
    AdminPermission.FINANCE_REFUND,
    AdminPermission.REWARDS_READ,
    AdminPermission.REWARDS_WRITE,
    AdminPermission.REWARDS_CONFIGURE,
    AdminPermission.ACADEMY_READ,
    AdminPermission.ACADEMY_WRITE,
    AdminPermission.ACADEMY_CERTIFY,
    AdminPermission.SUPPORT_READ,
    AdminPermission.SUPPORT_WRITE,
    AdminPermission.SUPPORT_ESCALATE,
    AdminPermission.MODERATION_READ,
    AdminPermission.MODERATION_APPROVE,
    AdminPermission.MODERATION_REJECT,
    AdminPermission.MODERATION_BAN,
    AdminPermission.AUDIT_READ,
    AdminPermission.AUDIT_EXPORT,
    AdminPermission.ANALYTICS_READ,
    AdminPermission.ANALYTICS_EXPORT,
    AdminPermission.CONFIG_READ,
    AdminPermission.NOTIFICATIONS_READ,
    AdminPermission.NOTIFICATIONS_WRITE,
    AdminPermission.NOTIFICATIONS_SEND,
  ],

  [AdminRole.MODERATOR]: [
    AdminPermission.USERS_READ,
    AdminPermission.CARDS_READ,
    AdminPermission.CARDS_MODERATE,
    AdminPermission.MODERATION_READ,
    AdminPermission.MODERATION_APPROVE,
    AdminPermission.MODERATION_REJECT,
    AdminPermission.SUPPORT_READ,
    AdminPermission.SUPPORT_WRITE,
  ],

  [AdminRole.FINANCE]: [
    AdminPermission.USERS_READ,
    AdminPermission.FINANCE_READ,
    AdminPermission.FINANCE_WRITE,
    AdminPermission.FINANCE_EXPORT,
    AdminPermission.FINANCE_REFUND,
    AdminPermission.SUBSCRIPTIONS_READ,
    AdminPermission.SUBSCRIPTIONS_WRITE,
    AdminPermission.SUBSCRIPTIONS_CANCEL,
    AdminPermission.PLANS_READ,
    AdminPermission.ANALYTICS_READ,
    AdminPermission.ANALYTICS_EXPORT,
  ],

  [AdminRole.SUPPORT]: [
    AdminPermission.USERS_READ,
    AdminPermission.CARDS_READ,
    AdminPermission.SUPPORT_READ,
    AdminPermission.SUPPORT_WRITE,
    AdminPermission.SUBSCRIPTIONS_READ,
    AdminPermission.NOTIFICATIONS_READ,
    AdminPermission.NOTIFICATIONS_SEND,
  ],

  [AdminRole.VIEWER]: [
    AdminPermission.USERS_READ,
    AdminPermission.CARDS_READ,
    AdminPermission.PARTNERS_READ,
    AdminPermission.PLANS_READ,
    AdminPermission.SUBSCRIPTIONS_READ,
    AdminPermission.FINANCE_READ,
    AdminPermission.REWARDS_READ,
    AdminPermission.ACADEMY_READ,
    AdminPermission.SUPPORT_READ,
    AdminPermission.MODERATION_READ,
    AdminPermission.AUDIT_READ,
    AdminPermission.ANALYTICS_READ,
    AdminPermission.CONFIG_READ,
    AdminPermission.NOTIFICATIONS_READ,
  ],
};

/**
 * Admin User Identity
 */
export interface AdminIdentity {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatar?: string;
  role: AdminRole;
  permissions: AdminPermission[];
}

/**
 * Check if role has specific permission
 */
export function hasPermission(
  role: AdminRole,
  permission: AdminPermission
): boolean {
  return RolePermissions[role]?.includes(permission) ?? false;
}

/**
 * Check if role has minimum level
 */
export function hasMinimumLevel(role: AdminRole, minLevel: number): boolean {
  return AdminRoleLevel[role] <= minLevel;
}

/**
 * Get all permissions for a role
 */
export function getPermissionsForRole(role: AdminRole): AdminPermission[] {
  return RolePermissions[role] ?? [];
}
